import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight, TreePine } from 'lucide-react';

const IntentDrillDown = ({ filters, reducedMotion = false }) => {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [treeData, setTreeData] = useState({ tree: [], meta: {} });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const animationDuration = reducedMotion ? 0 : 0.3;

  useEffect(() => {
    fetchTreeData();
  }, [filters]);

  const fetchTreeData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/insights/v2/intent-tree`);
      const result = await response.json();
      setTreeData(result);
      // Auto-expand first node
      if (result.tree.length > 0) {
        setExpanded(new Set([result.tree[0].id]));
      }
    } catch (error) {
      console.error('Error fetching intent tree:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    const newExpanded = new Set(expanded);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpanded(newExpanded);
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg text-slate-100">
          <TreePine className="w-5 h-5 text-green-400" />
          Intent Drill-Down Tree
        </CardTitle>
        <p className="text-xs text-slate-400 mt-1">
          Hierarchical intent exploration • Click to expand
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-pulse text-slate-400">Loading intent tree...</div>
          </div>
        ) : treeData.tree.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <p>No intent data available</p>
          </div>
        ) : (
          <div className="space-y-2">
            {treeData.tree.map((node, index) => (
              <motion.div
                key={node.id}
                initial={reducedMotion ? {} : { opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: animationDuration, delay: index * 0.05 }}
              >
                {/* Parent Node */}
                <div
                  className="bg-slate-800/50 rounded-lg p-4 border border-slate-700 cursor-pointer hover:border-gold/40 transition-colors"
                  onClick={() => toggleExpand(node.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {expanded.has(node.id) ? (
                        <ChevronDown className="w-4 h-4 text-slate-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      )}
                      <span className="text-sm font-semibold text-slate-100">{node.name}</span>
                      <span className="text-xs text-gold font-mono">{node.value} calls</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-slate-400">{node.percentage}%</div>
                      <div className="w-16 h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gold"
                          style={{ width: `${Math.min(node.percentage, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Children Nodes */}
                <AnimatePresence>
                  {expanded.has(node.id) && node.children && (
                    <motion.div
                      initial={reducedMotion ? {} : { height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={reducedMotion ? {} : { height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="ml-8 mt-2 space-y-1"
                    >
                      {node.children.map((child, childIdx) => (
                        <div
                          key={child.id}
                          className="bg-slate-800/30 rounded p-3 border border-slate-700/50"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-300">{child.name}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-gold font-mono">{child.value}</span>
                              <span className="text-xs text-slate-500">{child.percentage}%</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}

        {/* Stats */}
        {treeData.meta && (
          <div className="mt-6 grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-400">{treeData.meta.unique_intents || 0}</p>
              <p className="text-xs text-slate-400 mt-1">Parent Intents</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gold">
                {treeData.tree.reduce((sum, t) => sum + (t.children?.length || 0), 0)}
              </p>
              <p className="text-xs text-slate-400 mt-1">Sub-Outcomes</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-400">{treeData.meta.sample_size || 0}</p>
              <p className="text-xs text-slate-400 mt-1">Calls Analyzed</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default IntentDrillDown;
